import { EnquiryFormProvider } from '@context/EnquiryFormContext'
import { FormModalProvider } from '@context/FormModalContext'
import { OfficeSiteProvider } from '@context/OfficeSiteContext'
import { SiteSettingsContextProvider } from '@context/SiteSettingsContext'
import { UserContextProvider } from '@context/UserContext'
import FavouriteModal from '@global/FavouriteModal'
import Layout from '@global/Layout'
import { initGA, logPageView } from '@lib/analyticsService'
import { flatMenusToNav, mainMenusToNav } from '@lib/api'
import {
  getFlatMenus,
  getMainMenus,
  getSiteSettings,
} from '@lib/api-services/cmsPageService'
import facebookPixel from '@lib/facebookPixelHelper'
import { isNullOrEmpty } from '@lib/helpers'
import { ApiKeys } from '@lib/variables'
import { GoogleOAuthProvider } from '@react-oauth/google'
import { Router } from '@routes'
import * as Sentry from '@sentry/browser'
import '@styles/fonts.css'
import '@styles/global.css'
import { DefaultSeo } from 'next-seo'
import App from 'next/app'
import dynamic from 'next/dynamic'
import Head from 'next/head'
import Script from 'next/script'
import 'normalize.css'
import NProgress from 'nprogress'
import { Tooltip } from 'react-tooltip'
import 'react-tooltip/dist/react-tooltip.css'
import SEO from '../next-seo.config'
import '/static/nprogress.css'

const ToastNotification = dynamic(() => import('@global/ToastNotification'), {
  ssr: false,
})

Sentry.init({
  dsn: ApiKeys.SENTRY_DSN,
})
initGA()
class BarryPlantApp extends App {
  static async getInitialProps({ Component, ctx }) {
    let pageProps = {}

    if (Component.getInitialProps) {
      pageProps = await Component.getInitialProps(ctx)
      ctx.pageProps = pageProps
    }

    let menuData
    let settings

    const [flatMenusData, mainMenusData, siteSettings] = await Promise.all([
      getFlatMenus(),
      getMainMenus(),
      getSiteSettings(),
    ])

    const [footerMenus, accountMenus, megaNavMenus] =
      flatMenusToNav(flatMenusData)
    const mainMenu = mainMenusToNav(mainMenusData)

    menuData = {
      footerMenus,
      accountMenus,
      megaNavMenus,
      mainMenu,
    }

    settings = siteSettings

    return {
      pageProps,
      siteSettings: settings,
      menuData,
    }
  }

  timer = null
  componentDidCatch(error, errorInfo) {
    Sentry.withScope((scope) => {
      Object.keys(errorInfo).forEach((key) => {
        scope.setExtra(key, errorInfo[key])
      })

      Sentry.captureException(error)
    })

    super.componentDidCatch(error, errorInfo)
  }
  routeChangeStart = () => {
    clearTimeout(this.timer)
    this.timer = setTimeout(NProgress.start, 300)
  }

  routeChangeEnd = () => {
    clearTimeout(this.timer)
    NProgress.done()
  }

  componentDidMount() {
    // google analytics
    if (!window.GA_INITIALIZED) {
      initGA()
      window.GA_INITIALIZED = true
    }
    logPageView()
    facebookPixel.trackPageView()

    // page load progress
    Router.events.on('routeChangeStart', this.routeChangeStart)
    Router.events.on('routeChangeError', this.routeChangeEnd)
    Router.events.on('routeChangeComplete', () => {
      this.routeChangeEnd()
      logPageView()
      facebookPixel.trackPageView()
    })
  }

  componentWillUnmount() {
    // page load progress
    Router.events.off('routeChangeStart', this.routeChangeStart)
    Router.events.off('routeChangeComplete', this.routeChangeEnd)
    Router.events.off('routeChangeError', this.routeChangeEnd)
  }

  render() {
    const { pageProps, Component, menuData, siteSettings, ...ctx } = this.props
    let menu = { ...menuData }
    let settings = { ...siteSettings }

    const isHomePage = pageProps.wagtailPageType === 'HomePage'
    const isOfficePage = pageProps.wagtailPageType === 'OfficePage'
    const isHaveHeaderBlock =
      ['hero', 'hero_search', 'article_hero', 'full_screen_hero'].includes(
        pageProps?.pageData?.content_blocks?.[0]?.type
      ) ||
      (isOfficePage &&
        (pageProps?.pageData?.featured_image?.url ||
          pageProps?.pageData?.hero_video_url))

    const isTransparentHeader =
      isHaveHeaderBlock ||
      isHomePage ||
      [
        'CareersLandingPage',
        'AppraisalRequestPage',
        'LoginPage',
        'CreateAccountPage',
        'ThankYouPage',
      ].includes(pageProps.wagtailPageType) ||
      (!pageProps.wagtailPageType && isNullOrEmpty(pageProps))

    return (
      <>
        <Head>
          <meta
            name='viewport'
            content='initial-scale=1.0, width=device-width, user-scalable=yes'
          />
          <meta property='fb:app_id' content={ApiKeys.FACEBOOK_APP_ID} />
          <meta
            httpEquiv='Cache-Control'
            content='no-cache, no-store, must-revalidate'
          />
          <meta httpEquiv='Pragma' content='no-cache' />
          <meta httpEquiv='Expires' content='0' />
        </Head>
        {siteSettings?.thirdpartyapisettings?.facebook_pixel_id && (
          <Script
            id='fb-pixel'
            strategy='afterInteractive'
            dangerouslySetInnerHTML={{
              __html: `
                !function(f,b,e,v,n,t,s)
                {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
                n.callMethod.apply(n,arguments):n.queue.push(arguments)};
                if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
                n.queue=[];t=b.createElement(e);t.async=!0;
                t.src=v;s=b.getElementsByTagName(e)[0];
                s.parentNode.insertBefore(t,s)}(window, document,'script',
                'https://connect.facebook.net/en_US/fbevents.js');
                fbq('init', '${siteSettings?.thirdpartyapisettings?.facebook_pixel_id}');
                fbq('track', 'PageView');
              `,
            }}
          />
        )}
        <GoogleOAuthProvider clientId={ApiKeys.GOOGLE_OAUTH_ID}>
          <UserContextProvider ctx={ctx}>
            <SiteSettingsContextProvider siteSettings={{ ...settings }}>
              <OfficeSiteProvider>
                <FormModalProvider>
                  <EnquiryFormProvider>
                    <DefaultSeo {...SEO} />
                    <Layout
                      isHomePage={isHomePage}
                      isTransparentHeader={isTransparentHeader}
                      menuData={{ ...menu }}
                    >
                      <Component {...pageProps} />
                    </Layout>
                    <ToastNotification />
                    <FavouriteModal />
                    <Tooltip id='bp-tooltip' />
                  </EnquiryFormProvider>
                </FormModalProvider>
              </OfficeSiteProvider>
            </SiteSettingsContextProvider>
          </UserContextProvider>
        </GoogleOAuthProvider>
      </>
    )
  }
}
export default BarryPlantApp
